import React, { useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { FolderOutlined, LockOutlined } from '@ant-design/icons';
import { REFERENCE_MODULES } from '../../config/references.config.tsx';
import type { StageConfig, StageViewConfig } from '../../config/types';
import { ViewSwitcher, type ViewOption } from './ViewSwitcher';
import { useToolbarState } from '../../contexts/ToolbarContext';
import { useRbac } from '../../contexts/RbacContext';
import { useSalesStages, usePurchaseStages, useReconciliationStages } from '../../hooks/useStageConfig';
import { useMethodologyTree } from '../../hooks/useMethodology';
import {
  getMethodologyPath,
  getMethodologySections,
} from '../../features/methodology/methodologyTree';
import styles from '../../styles/components/SubHeaderStages.module.scss';

interface ModuleStageConfig {
  stages: StageConfig[];
  basePath: string;
}

function useStageNavigation() {
  const { pathname } = useLocation();
  const salesStages = useSalesStages();
  const purchaseStages = usePurchaseStages();
  const reconciliationStages = useReconciliationStages();
  // Вкладки методологии — разделы верхнего уровня; дерево грузим только в этом модуле
  const isMethodology = pathname.startsWith('/methodology/');
  const { data: methodologyNodes } = useMethodologyTree({ enabled: isMethodology });

  const methodologyStages = useMemo<StageConfig[]>(
    () => getMethodologySections(methodologyNodes ?? []).map((section) => ({
      title: section.title,
      description: section.title,
      icon: <FolderOutlined />,
      path: section.id,
    })),
    [methodologyNodes],
  );

  return useMemo(() => {
    const moduleStages: Record<string, ModuleStageConfig> = {
      sales: { stages: salesStages, basePath: '/sales' },
      purchase: { stages: purchaseStages, basePath: '/purchase' },
      reconciliation: { stages: reconciliationStages, basePath: '/reconciliation' },
      references: { stages: REFERENCE_MODULES, basePath: '/references' },
      methodology: { stages: methodologyStages, basePath: '/methodology' },
    };

    const segments = pathname.split('/').filter(Boolean);
    const moduleKey = segments[0];
    const config = moduleStages[moduleKey];
    if (!config) return null;

    // В методологии в URL id любой статьи — вкладка по её корневому разделу
    const stageSegment = moduleKey === 'methodology' && methodologyNodes
      ? getMethodologyPath(methodologyNodes, segments[1] ?? '')[0]?.id
      : segments[1];
    const stageIndex = config.stages.findIndex((s) => s.path === stageSegment);
    if (stageIndex < 0) return null;

    return { ...config, currentIndex: stageIndex, moduleKey };
  }, [pathname, salesStages, purchaseStages, reconciliationStages, methodologyStages, methodologyNodes]);
}

function toViewOptions(views: StageViewConfig[]): ViewOption[] {
  return views.map((v) => ({ value: v.key, label: v.label, icon: v.icon }));
}

const SLIDE_OFFSET = -8;
const MATERIAL_EASE = [0.4, 0, 0.2, 1] as const;

const containerVariants = {
  hidden: { opacity: 0, y: SLIDE_OFFSET },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: SLIDE_OFFSET },
};

const containerTransition = {
  duration: 0.25,
  ease: MATERIAL_EASE,
};

const pillTransition = {
  type: 'spring' as const,
  stiffness: 500,
  damping: 35,
};

const viewsRowTransition = {
  duration: 0.2,
  ease: MATERIAL_EASE as unknown as [number, number, number, number],
};

const SubHeaderStagesComponent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const navData = useStageNavigation();
  const toolbar = useToolbarState();
  const navRef = useRef<HTMLElement>(null);
  const { hasAnyChildPermission } = useRbac();

  const disabledStages = useMemo(() => {
    if (!navData) return new Set<number>();
    const disabled = new Set<number>();
    navData.stages.forEach((stage, index) => {
      if (stage.rbacResource && !hasAnyChildPermission(stage.rbacResource, 'read')) {
        disabled.add(index);
      }
    });
    return disabled;
  }, [navData, hasAnyChildPermission]);

  // Хуки вызываются до early return (правило React Hooks)
  const viewOptions = useMemo(() => {
    if (!navData) return [];
    const stage = navData.stages[navData.currentIndex];
    return stage.views ? toViewOptions(stage.views) : [];
  }, [navData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!navData) return;
      const { stages, basePath, currentIndex } = navData;
      let next: number | null = null;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (currentIndex + 1) % stages.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (currentIndex - 1 + stages.length) % stages.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = stages.length - 1;
          break;
      }

      if (next !== null && next !== currentIndex && !disabledStages.has(next)) {
        e.preventDefault();
        navigate(`${basePath}/${stages[next].path}`);
        const tabs = navRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
        tabs?.[next]?.focus();
      }
    },
    [navData, navigate, disabledStages],
  );

  if (!navData) return null;

  const { stages, basePath, currentIndex, moduleKey } = navData;
  const currentStage = stages[currentIndex];
  const views = currentStage.views;
  const hasViews = views && views.length > 0;

  // Есть ли toolbar-actions (filters / additionalControls); reload теперь в хедере
  const hasToolbarActions = !!(toolbar.filters || toolbar.additionalControls);

  // Views row — когда есть представления или есть filters/additionalControls
  const showViewsRow = hasViews || hasToolbarActions;

  // Текущее представление из URL (?view=xxx), fallback на первый view
  // (в т.ч. если представление из URL недоступно в выбранном году)
  const requestedView = searchParams.get('view');
  const currentView = (requestedView && views?.some((v) => v.key === requestedView))
    ? requestedView
    : views?.[0]?.key ?? '';

  const handleViewChange = (viewKey: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', viewKey);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <motion.div
      className={styles.subHeader}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={containerTransition}
    >
      {/* Строка 1: Этапы (скрываем если этап всего один) */}
      {/* В методологии вкладки — разделы, показываем и единственный */}
      {(stages.length > 1 || moduleKey === 'methodology') && <div className={styles.stagesRow}>
        <LayoutGroup id={moduleKey}>
          <nav
            ref={navRef}
            className={styles.navPill}
            role="tablist"
            aria-label={moduleKey === 'methodology' ? 'Разделы методологии' : 'Этапы планирования'}
            onKeyDown={handleKeyDown}
            data-tour-id="stages-navigation"
          >
            {stages.map((stage, index) => {
              const isActive = index === currentIndex;
              const isDisabled = disabledStages.has(index);
              // Полное название — в тултипе/aria, т.к. на узком экране описание обрезается
              const fullLabel = [stage.title, stage.description !== '-' ? stage.description : '']
                .filter(Boolean)
                .join(' · ');
              const tooltipTitle = isDisabled
                ? 'У вас нет доступа к этому этапу'
                : fullLabel;

              return (
                <Tooltip
                  key={stage.path}
                  title={tooltipTitle}
                  placement="bottom"
                  mouseEnterDelay={0.5}
                >
                  <button
                    role="tab"
                    aria-selected={isActive}
                    aria-disabled={isDisabled}
                    aria-label={fullLabel}
                    tabIndex={isActive ? 0 : -1}
                    className={`${styles.stageTab} ${isActive ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}
                    onClick={() => !isDisabled && navigate(`${basePath}/${stage.path}`)}
                    type="button"
                    data-tour-id={`stage-button-${index}`}
                  >
                    {isActive && !isDisabled && (
                      <motion.span
                        className={styles.activePill}
                        layoutId="stage-active-pill"
                        transition={pillTransition}
                      />
                    )}

                    <span className={styles.stageBadge}>
                      {isDisabled ? <LockOutlined className={styles.lockIcon} /> : index + 1}
                    </span>
                    {stage.description !== '-' && (
                      <span className={styles.stageDesc}>{stage.description}</span>
                    )}
                  </button>
                </Tooltip>
              );
            })}
          </nav>
        </LayoutGroup>
      </div>}

      {/* Строка 2: Views + Actions (только при наличии представлений) */}
      <AnimatePresence>
        {showViewsRow && (
          <motion.div
            key="views-row"
            className={styles.viewsRow}
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={viewsRowTransition}
          >
            <div className={styles.viewsRowInner}>
              {hasViews && (
                <ViewSwitcher
                  options={viewOptions}
                  value={currentView}
                  onChange={handleViewChange}
                  layoutId={`${moduleKey}-${currentStage.path}-views`}
                />
              )}

              {hasToolbarActions && (
                <div className={styles.toolbarActions}>
                  {toolbar.additionalControls}
                  {toolbar.filters}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const SubHeaderStages = React.memo(SubHeaderStagesComponent);
